import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { DropTarget } from 'react-dnd';
import {
  FLAGS_NONREMOVABLE,
  FIELD_NAMES,
  FIELD_NAMES_ADDED,
  FIELD_RULES,
  GDPR_SETTING,
  CREDENTIAL_TYPES,
  INFERRABLE_FIELD_NAME_MAP,
  COMMON_EDITOR_OVERRIDE_FIELDS,
  LEAD_SHARE_OVERRIDE_FIELDS,
} from 'utils/constants';
import { generateHTMLIdFromKeyPath } from 'utils/idUtils';
import DraggableItemTypes from 'components/draggable.item.types';
import Modal from 'components/Modal';
import ConditionalityEditor from './ConditionalityEditors';
import ThankYouRouteEditor from './ThankYouRouteEditor';
import FieldCommonEditor from './FieldCommonEditor';
import SectionNumber from '../SectionNumber';
import FieldEditorOptInOverride from './FieldEditorOptInOverride';
import FieldHeader from './FieldHeader';
import FieldEditorFactory from './FieldEditorFactory';
import CheckBox from './CheckBoxAttribute';
import { THANK_YOU_ROUTING_FIELD_TYPES } from '../../utils/conditionalConstants';

/**
 * Maps PASS_CONSENT_FOR_ALL_USERS to the effective GDPR setting for UI rendering based on credential type
 */
function getEffectiveGdprSetting(gdprSetting, availableCredentialTypes) {
  if (gdprSetting !== GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS) {
    return gdprSetting;
  }

  const credentialType = availableCredentialTypes?.[0]?.value;

  if (credentialType === CREDENTIAL_TYPES.DEGREE) {
    return GDPR_SETTING.GEO_IMPLIED;
  }

  if (credentialType === CREDENTIAL_TYPES.SHORT_COURSE) {
    return GDPR_SETTING.ALWAYS;
  }

  return gdprSetting;
}

const FormFieldDropTarget = {
  drop(props) {
    const branchingStep = props.activeBranchingFieldDetails.length ? props.activeBranchingFieldDetails[0].step : null;
    return {
      targetIndex: props.index,
      targetStep: props.step,
      branchingStep,
    };
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOverCurrent: monitor.isOver({ shallow: true }),
    otherItem: monitor.getItem(),
  };
}

function DropToPlaceHerePrompt() {
  const style = {
    borderWidth: '2px',
    borderStyle: 'dashed',
    borderColor: '#ccc',
  };

  return (
    <div className="place-card-here text-center p-3 m-2 text-muted" style={style}>
      Release to place card here
    </div>
  );
}

class FormFieldAttributes extends React.Component {
  state = {
    beingDragged: false,
    modal: {
      show: false,
    },
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  onModalCancel = () => {
    if (this.state.modal.onCancel) {
      this.state.modal.onCancel();
    }
    this.setState(() => ({ modal: { show: false } }));
  }

  onModalConfirm = () => {
    if (this.state.modal.onConfirm) {
      this.state.modal.onConfirm();
    }
    this.setState(() => ({ modal: { show: false } }));
  }

  setBorderClass = (errorTree, active) => {
    let borderClass = '';

    if (errorTree && this.checkErrorsVisible(errorTree)) {
      borderClass = borderClass.concat(' border-card-error');
    }

    if (active) {
      borderClass = borderClass.concat(' border-card-active');
    }
    return borderClass;
  }

  checkErrorsVisible = (errorTree) => {
    let visibleErrors = false;

    Object.keys(errorTree).forEach((key) => {
      if (errorTree[key].error && errorTree[key].error.visible) {
        visibleErrors = true;
      } else if (key !== 'error') {
        visibleErrors = this.checkErrorsVisible(errorTree[key]);
      }
    });

    return visibleErrors;
  }

  displayRulesStatus = (values) => {
    const rules = [];
    if (values.required) {
      rules.push(FIELD_RULES.REQUIRED);
    }
    if (values.hidden) {
      rules.push(FIELD_RULES.HIDDEN);
    }
    if (values.name === FIELD_NAMES.CONSENT_NON_GDPR) {
      rules.push(FIELD_RULES.US_ONLY);
    }
    if (values.name === FIELD_NAMES.CONSENT_GDPR) {
      rules.push(FIELD_RULES.GDPR_ONLY);
    }
    if (values.name === FIELD_NAMES.PHONE) {
      rules.push(FIELD_RULES.GDPR_OPTIONAL);
    }

    return rules.join(', ');
  }

  toggleModal = ({ onConfirm = null, onCancel = null }) => {
    this.setState(({ modal }) => ({ modal: { show: !modal.show, onCancel, onConfirm } }));
  }

  render() {
    let requiredStandardField = this.props.values.flags && this.props.values.flags.includes(FLAGS_NONREMOVABLE);
    if (this.props.areAllFieldsOptional) requiredStandardField = false;
    const rulesStatus = this.displayRulesStatus(this.props.values);
    const otherItemIsHoveringOverUs = this.props.isOverCurrent && (this.props.otherItem.keyPath !== this.props.keyPath);
    const borderClasses = this.setBorderClass(this.props.errors, this.props.uiState.isActive);
    const fieldTypeEditorRenderFunction = FieldEditorFactory(this.props.values);
    let sfDetails = {
      dataMissing: true,
      sfFieldType: 'Missing from passport',
    };
    const fieldName = this.props.values?.name;
    if (this.props.values) {
      if (this.props.sfFieldData[fieldName]) {
        sfDetails = this.props.sfFieldData[fieldName];
      } else if (FIELD_NAMES_ADDED.includes(fieldName)) {
        sfDetails = {
          sfFieldType: 'Not a SalesForce field',
        };
      }
    }
    const isBranchingField = this.props.fieldConditionalityInfo.branchingFields[this.props.values.id] !== undefined;
    const isThankYouField = (this.props.thankYouRoutingFields && !!this.props.thankYouRoutingFields[this.props.values.id]) || !this.props.thankYouRoutingFields;
    let branchingQuestion = {};
    let branchMembership = [];

    if (this.props.activeBranchingFieldDetails.length) {
      // Only one field is currently allowed as branching.
      branchingQuestion = this.props.activeBranchingFieldDetails[0]; // eslint-disable-line prefer-destructuring
      branchMembership = this.props.fieldConditionalityInfo.graph.getNearestBranchVerticesForFieldVertex({ left: this.props.values.id }, branchingQuestion.id);
    }
    const memberBranchIds = branchMembership.map((branchVal) => this.props.activeBranches[branchingQuestion.id]
      .find((branch) => branch.value === branchVal)
    ).map(({ index }) => index + 1).sort((a, b) => a - b);

    const displayInferredCheckbox = INFERRABLE_FIELD_NAME_MAP[fieldName] && !branchMembership.length;
    const inferredCheckboxLabelText = () => {
      const { helpText, checkboxText } = INFERRABLE_FIELD_NAME_MAP[fieldName];
      const boxTextId = generateHTMLIdFromKeyPath(this.props.keyPath, 'bx-tx');
      const helpTextId = generateHTMLIdFromKeyPath(this.props.keyPath, 'hlp-tx');
      return helpText ?
        <><p id={boxTextId}>{checkboxText}</p><p id={helpTextId} className="text-muted">{helpText}</p></> :
        checkboxText;
    };

    const shouldDisableRulesWhenInferred = this.props.isInferredField &&
      INFERRABLE_FIELD_NAME_MAP[fieldName] &&
      INFERRABLE_FIELD_NAME_MAP[fieldName].shouldHideField;

    const disableRulesContainer = requiredStandardField ||
      shouldDisableRulesWhenInferred ||
      !fieldName ||
      fieldName === FIELD_NAMES.CP_DEGREE_OFFERING ||
      (fieldName === FIELD_NAMES.PHONE && this.props.gdprSetting === GDPR_SETTING.ALWAYS_IMPLIED);

    const ConditionalitySection = !this.props.isInferredField && ConditionalityEditor({
      ...this.props,
      branchingQuestion,
      branchMembership,
      isBranchingField,
      toggleBranchMembership: this.props.toggleBranchMembership,
      step: this.props.step,
    });

    const canRenderThankYouRouting = !this.props.isInferredField && THANK_YOU_ROUTING_FIELD_TYPES.includes(this.props.values.type);

    const ThankYouPageRouting = canRenderThankYouRouting && (
      <ThankYouRouteEditor
        toggleThankYouRoute={this.props.toggleThankYouRoute}
        values={this.props.values}
        keyPath={this.props.keyPath}
        isThankYouField={isThankYouField}
        options={this.props.thankYouRoutingFields[this.props.values.id]}
        setThankYouRoute={this.props.setThankYouRoute}
        refreshThankYouRoute={this.props.refreshThankYouRoute}
        fieldValidityChanged={this.props.fieldValidityChanged}
        invalidFormData={this.props.invalidFormData}
      />
    );

    const fieldBodyClickHandler = (event) => {
      event.stopPropagation(); // Don't let the click event bubble up to the FormBuilderView
      this.props.fieldClicked(this.props.keyPath[1]);
    };
    const usesCommonEditorOverride = COMMON_EDITOR_OVERRIDE_FIELDS.includes(fieldName);
    const effectiveGdprSetting = getEffectiveGdprSetting(this.props.gdprSetting, this.props.availableCredentialTypes);
    const isGeoImplied = effectiveGdprSetting === GDPR_SETTING.GEO_IMPLIED;
    const showOverrideEditor = usesCommonEditorOverride && isGeoImplied;
    const isLeadShareField = LEAD_SHARE_OVERRIDE_FIELDS.includes(fieldName) && this.props.values.type === 'picklist_value_opt_in';
    const showEditorOverride = isLeadShareField && isGeoImplied;

    const FieldBody = this.props.uiState.isCollapsed ? null : (
      <div className="container-fluid pl-0 pb-1">
        <div className="row ml-0 mr-1">
          <div className="col-1 p-0">
            <SectionNumber number={(showOverrideEditor) ? '1A' : '1'} />
          </div>
          <FieldCommonEditor
            disableRulesContainer={disableRulesContainer}
            fieldValidityChanged={this.props.fieldValidityChanged}
            fieldNameDropdownOpts={this.props.fieldNameDropdownOpts}
            gdprSetting={this.props.gdprSetting}
            isBranchingField={isBranchingField}
            isThankYouField={isThankYouField}
            keyPath={this.props.keyPath}
            loadInProgress={this.props.loadInProgress}
            onFieldNameChange={this.props.onFieldNameChange}
            onFieldUIStateChange={this.props.onFieldUIStateChange}
            onFieldValueChange={this.props.onFieldValueChange}
            requiredStandardField={requiredStandardField}
            sfFieldData={this.props.sfFieldData}
            sfDetails={sfDetails}
            supportsDegreeOffering={this.props.supportsDegreeOffering}
            availableCredentialTypes={this.props.availableCredentialTypes}
            uiState={this.props.uiState}
            values={this.props.values}
            showOverrideEditor={showOverrideEditor}
          />
        </div>
        <div className="row mx-0 border-top border-secondary pb-3">
          <div className="col-1 p-0">
            <SectionNumber number={(showEditorOverride) ? '2A' : '2'} />
          </div>
          <div className="col-11">
            <div className="row">
              <div className="col-12 pt-3 pl-0">
                {fieldTypeEditorRenderFunction({
                  ...this.props,
                  keyPath: [...this.props.keyPath, 'editors'],
                })}
              </div>
              {
                displayInferredCheckbox ? (
                  <div className="col-12 px-3 pt-3">
                    <CheckBox
                      value={this.props.isInferredField}
                      keyPath={[...this.props.keyPath, 'inferField']}
                      onCheckBoxChange={(_, value) => {
                        this.props.setFieldInferredState(this.props.values.name, value, this.props.keyPath[1]);
                      }}
                    >
                      {inferredCheckboxLabelText()}
                    </CheckBox>
                  </div>
                ) : null
              }
            </div>
          </div>
        </div>
        {showEditorOverride && (
          <div className="col-12">
            <div className="row">
              <div className="col-12 pt-3 pl-0">
                <FieldEditorOptInOverride
                  keyPath={[...this.props.keyPath, 'overrides', 'gdpr', 'editors']}
                  uiState={this.props.uiState}
                  values={this.props.values}
                  onFieldValueChange={this.props.onFieldValueChange}
                  onFieldUIStateChange={this.props.onFieldUIStateChange}
                  disabled={false}
                />
              </div>
            </div>
          </div>
        )}

        {ConditionalitySection ? (
          <div className="row mx-0 border-top border-secondary">
            <div className="col-1 p-0">
              <SectionNumber number={3} />
            </div>
            <div className="col-11 pt-3 pl-0">
              {ConditionalitySection}
            </div>
          </div>
        ) : null}
        {ThankYouPageRouting ? (
          <div className="row mx-0 border-top border-secondary">
            <div className="col-1 p-0">
              <SectionNumber number={ConditionalitySection ? 4 : 3} />
            </div>
            <div className="col-11 pt-3 pl-0">
              {ThankYouPageRouting}
            </div>
          </div>
        ) : null}
      </div>
    );
    return this.props.connectDropTarget(
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
      <div role="heading" onClick={fieldBodyClickHandler}>
        {otherItemIsHoveringOverUs ? <DropToPlaceHerePrompt /> : null}
        <div
          id={generateHTMLIdFromKeyPath(this.props.keyPath, 'tx')}
          className={`card shadow-sm taxi-card ${borderClasses}`}
          style={{ opacity: this.state.beingDragged ? 0.35 : 1 }}
        >
          <FieldHeader
            branchMembership={memberBranchIds}
            keyPath={this.props.keyPath}
            index={this.props.index}
            isBranchingField={isBranchingField}
            isThankYouField={isThankYouField}
            isRequiredField={requiredStandardField}
            onDelete={() => this.props.onDeleteField(this.props.keyPath, this.props.index)}
            onDrag={() => this.setState({ beingDragged: true })}
            onDrop={() => this.setState({ beingDragged: false })}
            removeAllUserBranchMembership={() => this.props.removeAllUserBranchMembership(this.props.values.id)}
            reorderField={this.props.reorderField}
            rightText={rulesStatus}
            toggleModal={this.toggleModal}
            title={this.props.title}
            toggleFieldCollapsedState={this.props.toggleFieldCollapsedState}
          />
          {FieldBody}
        </div>
        {
          this.state.modal.show ? (
            <Modal>
              <Modal.Header
                onCancel={this.onModalCancel}
              >
                <h5 className="modal-title">Reorder Field</h5>
              </Modal.Header>
              <Modal.Body>
                This field contains branching. Moving this field will remove all conditional logic.
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={this.onModalCancel}>Cancel</button>
                <button type="button" className="btn btn-info" onClick={this.onModalConfirm}>Reorder Field</button>
              </Modal.Footer>
            </Modal>
          ) : null
        }
      </div>
    );
  }
}

FormFieldAttributes.propTypes = {
  activeBranchingFieldDetails: PropTypes.array.isRequired,
  activeBranches: PropTypes.object.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    fields: PropTypes.object,
    screens: PropTypes.object,
  }),
  fieldClicked: PropTypes.func.isRequired,
  fieldConditionalityInfo: PropTypes.shape({
    branchingFields: PropTypes.object.isRequired,
    graph: PropTypes.object.isRequired,
  }).isRequired,
  areAllFieldsOptional: PropTypes.bool,
  thankYouRoutingFields: PropTypes.object.isRequired,
  fieldNameDropdownOpts: PropTypes.object.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  gdprSetting: PropTypes.string.isRequired, // eslint-disable-line
  otherItem: PropTypes.shape({
    keyPath: PropTypes.array,
  }),
  index: PropTypes.number.isRequired,
  isOverCurrent: PropTypes.bool,
  isInferredField: PropTypes.bool.isRequired,
  keyPath: PropTypes.array.isRequired,
  loadInProgress: PropTypes.bool,
  onDeleteField: PropTypes.func.isRequired,
  onFieldNameChange: PropTypes.func.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  removeAllUserBranchMembership: PropTypes.func.isRequired,
  reorderField: PropTypes.func.isRequired,
  setFieldInferredState: PropTypes.func.isRequired,
  sfFieldData: PropTypes.object.isRequired,
  step: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  toggleBranching: PropTypes.func.isRequired, // eslint-disable-line
  toggleThankYouRoute: PropTypes.func.isRequired,
  setThankYouRoute: PropTypes.func.isRequired,
  supportsDegreeOffering: PropTypes.bool.isRequired,
  availableCredentialTypes: PropTypes.array,
  refreshThankYouRoute: PropTypes.func.isRequired,
  toggleBranchMembership: PropTypes.func.isRequired,
  toggleFieldCollapsedState: PropTypes.func.isRequired,
  uiState: PropTypes.shape({
    editors: PropTypes.shape({
      degreeOfferingConfig: PropTypes.object,
      radioButtonConfig: PropTypes.object,
      optInData: PropTypes.object,
    }).isRequired,
    label: PropTypes.object,
    name: PropTypes.object,
    type: PropTypes.object,
    value: PropTypes.object,
    isCollapsed: PropTypes.bool,
    isActive: PropTypes.bool,
  }),
  invalidFormData: PropTypes.object.isRequired,
  values: PropTypes.shape({
    editors: PropTypes.shape({
      dropdownConfig: PropTypes.object,
      optInData: PropTypes.object,
      gdprConsentData: PropTypes.object,
      radioButtonConfig: PropTypes.object,
      degreeOfferingConfig: PropTypes.object,
      value: PropTypes.string,
    }).isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    hidden: PropTypes.bool.isRequired,
    flags: PropTypes.arrayOf(PropTypes.string),
  }),
};

FormFieldAttributes.defaultProps = {
  areAllFieldsOptional: false,
  errors: {},
  loadInProgress: false,
  isOverCurrent: false,
  otherItem: null,
  availableCredentialTypes: [],
  values: {
    flags: [],
  },
  uiState: {},
};

export default DropTarget([DraggableItemTypes.FIELD_ITEM, DraggableItemTypes.STEP_ITEM], FormFieldDropTarget, collect)(FormFieldAttributes);
